import { Button } from "@/app/components/ui/button";
import { NoSsr } from "@mui/material";
import { Auction, AuctionSettings } from "@prisma/client";
import { Flex, Text } from "@radix-ui/themes";
import cn from "classnames";
import { format } from "date-fns";
import Link from "next/link";
import React, { useState } from "react";

interface Props {
  auction: Auction;
  auctionSettings: AuctionSettings;
  className?: string;
}

const dateFormat = "h:mma 'on' EEEE, MMMM do";

const AuctionInfo = ({ auction, auctionSettings, className }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);
  const [shouldShowButton, setShouldShowButton] = useState(false);

  React.useEffect(() => {
    // Check if content overflows the container when clamped to 3 lines
    const checkOverflow = () => {
      const element = textRef.current;
      if (!element) return;

      // Get line height and total height
      const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * 3; // 3 lines max when not expanded

      // Compare scroll height (full content) to max allowed height
      const hasOverflow = element.scrollHeight > maxHeight;
      setShouldShowButton(hasOverflow);
    };

    checkOverflow();
    // Recheck on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [auctionSettings.description]);

  const toggleText = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Flex
      direction="column"
      className={cn(
        "border pt-5 px-3 pb-4 w-full md:w-2/3 min-h-72 bg-zinc-100",
        className
      )}
      gap="5"
    >
      <h2>{auction.title}</h2>
      <Flex direction="column" gap="1">
        <Text className="font-semibold">
          Opens:{" "}
          <NoSsr>
            <Text className="font-normal" suppressHydrationWarning>
              {format(auction.startDate, dateFormat)}
            </Text>
          </NoSsr>
        </Text>
        <Text className="font-semibold">
          Closes:{" "}
          <NoSsr>
            <Text className="font-normal font-sm" suppressHydrationWarning>
              {format(auction.endDate, dateFormat)}
            </Text>
          </NoSsr>
        </Text>
      </Flex>
      <Flex
        direction="column"
        justify="center"
        className={cn(`w-full mt-4`)}
        gap="4"
      >
        <Text
          ref={textRef}
          className={cn(
            `transition-all duration-300 ease-in-out text-sm font-medium whitespace-pre-line`,
            isExpanded && "line-clamp-none",
            !isExpanded && "line-clamp-3"
          )}
        >
          {auctionSettings.description || "Description here..."}
        </Text>
        {shouldShowButton && (
          <Flex justify="center">
            <Button className="w-24" variant="ghost" onClick={toggleText}>
              {isExpanded ? "See less" : "See more"}
            </Button>
          </Flex>
        )}
      </Flex>
      <div className="flex flex-col items-end sm:flex-row sm:justify-end justify-center gap-2">
        <Link href={`/auctions/${auction.id}/terms`}>
          <Button variant="outline">
            <Text className="text-sm font-medium">Terms and Conditions</Text>
          </Button>
        </Link>
        <Link
          href={`https://s3.us-east-1.amazonaws.com/biddify.tenant.assets/${process.env.TENANT_ID}/abf.pdf`}
          target="_blank"
        >
          <Button variant="outline">
            <Text className="text-sm font-medium">Absentee Bid Form</Text>
          </Button>
        </Link>
      </div>
    </Flex>
  );
};

export default AuctionInfo;
